import angular from "angular";

import { Ajax, Test } from "@salaxy/core";

import { UiHelpers, SessionService } from "../../services";

/**
 * Controller for resetting account data in the test environment.
 */
export class AccountResetController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Test",
    "UiHelpers",
    "AjaxNg1",
    "SessionService"
  ];

  /**
   * Creates a new AccountResetController.
   * @param testApi - Api methods for resetting account data.
   * @param uiHelpers - Salaxy UI Helpers.
   * @param ajax - Salaxy ajax component.
   */
  constructor(
    private testApi: Test,
    private uiHelpers: UiHelpers,
    private ajax: Ajax,
    private sessionService: SessionService
  ) { }

  /**
   * Delete the current account data and credentials (including Auth0 user).
   * Can be called only in a test environment.
   */
  public deleteCurrent(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccount().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove all calculations, workers etc. user objects except products and signature from the account.
   * Can be called only in a test environment.
   */
  public deleteData(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tiliin liittyvän datan?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountData().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove all calculations, payrolls and payments from the account.
   * Can be called only in a test environment.
   */
  public deleteCalculations(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin laskelmat?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountCalculations().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove workers including calculations, employment contracts and tax cards from the account.
   * Can be called only in a test environment.
   */
  public deleteWorkers(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin työntekijät?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountWorkers().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove all holiday year from all workers. Does not touch the default values of holidays in Worker Employment relation.
   * Can be called only in a test environment.
   */
  public deleteHolidays(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa loma- ja poissaolotiedot?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountHolidays().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Delete all empty accounts (company or worker) created by this account.
   * Can be called only in a test environment.
   */
  public deleteAuthorizingAccounts(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin luomat muut tilit?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountAuthorizingAccounts().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove pension and insurance from the account.
   * Can be called only in a test environment.
   */
  public deletePensionAndInsurance(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tiliin liittyvän TyEL:n ja vakuutukset?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountPensionAndInsurance().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Remove the signature from the account.
   * Can be called only in a test environment.
   */
  public deleteSignature(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin valtuutuksen (allekirjoitus)?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountSignature().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Checks if the KYC (Know Your Customer) process is done for the current account.
   * @returns A boolean value indicating whether the KYC process is done.
   */
  public isKycMissing(): boolean {
    return !((this.sessionService.session?.currentAccount?.identity as any)?.kycUpdatedAt);
  }

  /**
   * Handles the KYC (Know Your Customer) process.
   * If KYC is missing, it activates the KYC.
   * Otherwise, it deletes the KYC.
   */
  public handleKyc(): void {
    if (this.isKycMissing()) {
      this.activateKyc();
    } else {
      this.deleteKyc();
    }
  }

  /**
   * Deletes the KYC (Know Your Customer) information for the current account.
   * Shows a confirmation dialog before deleting the KYC.
   * If the user confirms, it makes an API call to delete the KYC and logs out the user.
   */
  private deleteKyc(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti poistaa tilin KYCin?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.deleteCurrentAccountKyc().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  /**
   * Activates the KYC for the account.
   * Displays a confirmation dialog and proceeds with the activation if confirmed.
   * Deletes the current account signature and logs out after successful activation.
   */
  private activateKyc(): void {
    this.uiHelpers.showConfirm("Haluatko varmasti aktivoidatilin KYCin?")
      .then((result: boolean) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.testApi.activateCurrentAccountKyc().then(() => {
            loading.dismiss();
            this.logout();
          });
        }
      });
  }

  private logout() {
    this.ajax.setCurrentToken(null);
    window.location.assign("/");
  }
}
